

import React, { useState, useEffect } from 'react';
import { db } from './Firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import Navbar from './Navbar';

const AdminFeedbackPage = () => {
  const [orders, setOrders] = useState([]);
  const [feedbacks, setFeedbacks] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [feedbackFilter, setFeedbackFilter] = useState('all');

  useEffect(() => {
    const fetchOrdersAndFeedback = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'Billing'));
        const fetchedOrders = [];
        const feedbackData = {};

        for (const docSnap of querySnapshot.docs) {
          const orderData = docSnap.data();
          fetchedOrders.push({ ...orderData, id: docSnap.id });

          const feedbackDocRef = doc(db, 'Feedback', docSnap.id);
          const feedbackDocSnap = await getDoc(feedbackDocRef);

          feedbackData[docSnap.id] = feedbackDocSnap.exists() ? feedbackDocSnap.data() : null;
        }

        fetchedOrders.sort((a, b) => {
          const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(0);
          const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(0);
          return bTime - aTime;
        });

        setOrders(fetchedOrders);
        setFeedbacks(feedbackData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching orders or feedback:", error);
        setLoading(false);
      }
    };

    fetchOrdersAndFeedback();
  }, []);

  const copyFeedbackLink = (orderId) => {
    const link = `https://products-5dbf3.web.app/FeedbackUser?orderId=${orderId}`;
    navigator.clipboard.writeText(link).then(() => {
      Swal.fire("Copied!", "Feedback link copied to clipboard.", "success");
    }).catch(() => {
      Swal.fire("Error", "Failed to copy link.", "error");
    });
  };

  const sendWhatsAppFeedbackRequest = (order) => {
    const phone = order.contact || order.phone;
    if (!phone) {
      Swal.fire("No Contact", "No phone number available for this user.", "warning");
      return;
    }

    const formattedPhone = phone.startsWith('+') ? phone.replace('+', '') : `91${phone.replace(/\D/g, '')}`;
    const feedbackLink = `https://products-5dbf3.web.app/FeedbackUser?orderId=${order.id}`;

    const message = `Hello 👋 ${order.name || 'Customer'},%0A%0AThank you for your recent order! 🙌%0A%0AWe'd love to hear your thoughts. Please take a moment to share your feedback by clicking below: ✍️%0A👉 ${feedbackLink}%0A%0AYour feedback helps us serve you better 💛%0A%0A- Team KP Bakers 🍰`;

    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const filteredOrders = orders
    .filter(order => {
      const name = String(order.name || '').toLowerCase();
      const email = String(order.email || '').toLowerCase();
      const orderId = String(order.orderId || '').toLowerCase();
      return (
        name.includes(searchTerm) ||
        email.includes(searchTerm) ||
        orderId.includes(searchTerm)
      );
    })
    .filter(order => {
      const hasFeedback = feedbacks[order.id] != null;
      if (feedbackFilter === 'done') return hasFeedback;
      if (feedbackFilter === 'pending') return !hasFeedback;
      return true;
    });

  return (
    <>
      <Navbar />
      <div style={styles.container} className="container-fluid py-5">
        <h1 className="text-center text-white mb-5 display-4 fw-bold">Admin - Feedback Tracker</h1>

        <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <input
              type="text"
              className="form-control shadow-sm"
              placeholder="Search by name, email, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
              style={{ backgroundColor: '#1f1f1f', color: '#fff', border: '1px solid #555' }}
            />
          </div>
          <div className="col-md-6">
            <select
              className="form-select shadow-sm"
              value={feedbackFilter}
              onChange={(e) => setFeedbackFilter(e.target.value)}
              style={{ backgroundColor: '#1f1f1f', color: '#fff', border: '1px solid #555' }}
            >
              <option value="all">All Orders</option>
              <option value="done">Feedback Done</option>
              <option value="pending">Feedback Pending</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading orders...</div>
        ) : (
          <div className="row">
            {filteredOrders.length === 0 ? (
              <p className="text-white text-center">No orders match the filter.</p>
            ) : (
              filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="col-md-6 col-lg-4 mb-4"
                >
                  <div
                    className="p-4 shadow-lg rounded text-light h-100"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <h5 className="fw-semibold mb-2">{order.name || 'Unnamed User'}</h5>
                    <p><strong>Order ID:</strong> {order.orderId || 'N/A'}</p>
                    <p><strong>Email:</strong> <a href={`mailto:${order.email}`} style={{ color: '#0dcaf0' }}>{order.email || 'No Email'}</a></p>
                    <p><strong>Contact:</strong> {order.contact || order.phone || 'N/A'}</p>
                    <p><strong>Total:</strong> ₹{order.totalAmount || 0}</p>
                    <p><strong>Feedback Rating:</strong>{' '}
                      {feedbacks[order.id] ? (
                        <span style={{ color: '#ffc107' }}>★ {feedbacks[order.id].rating}</span>
                      ) : (
                        <span className="text-muted">No Feedback Yet</span>
                      )}
                    </p>
                    <p><strong>Feedback Link:</strong><br />
                      <a
                        href={`https://products-5dbf3.web.app/FeedbackUser?orderId=${order.id}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#0dcaf0' }}
                      >
                        Open Feedback Page
                      </a>
                    </p>
                    <div className="d-grid gap-2">
                      <button
                        className="btn btn-outline-light mt-2"
                        onClick={() => copyFeedbackLink(order.id)}
                      >
                        Copy Feedback Link
                      </button>
                      <button
                        className="btn btn-success mt-2"
                        onClick={() => sendWhatsAppFeedbackRequest(order)}
                      >
                        Send WhatsApp Request
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </>
  );
};

const styles = {
  container: {
    background: 'linear-gradient(to right, #141e30, #243b55)',
    minHeight: '100vh',
    borderRadius: '10px',
  },
  loading: {
    color: 'white',
    fontSize: '20px',
    textAlign: 'center',
    padding: '20px',
  },
};

export default AdminFeedbackPage;
